import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { MdStar, MdFormatListBulleted, MdAttachMoney } from 'react-icons/md';
import DashboardLayout from '../components/DashboardLayout.js';
import './Dashboard.css';

function Dashboard() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (!user.userId) return;
    async function fetchDashboardData() {
      try {
        const [dashRes, eventsRes, tasksRes, leadsRes, salesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/dashboard?userId=${user.userId}`),
          fetch(`${API_BASE_URL}/events?userId=${user.userId}`),
          fetch(`${API_BASE_URL}/tasks?userId=${user.userId}`),
          fetch(`${API_BASE_URL}/leads?userId=${user.userId}`),
          fetch(`${API_BASE_URL}/sales?userId=${user.userId}`),
        ]);
        if (dashRes.ok) setStats(await dashRes.json());
        if (eventsRes.ok) setEvents(await eventsRes.json());
        if (tasksRes.ok) setTasks(await tasksRes.json());
        if (leadsRes.ok) setLeads(await leadsRes.json());
        if (salesRes.ok) setSales(await salesRes.json());
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchDashboardData();
  }, [user.userId, API_BASE_URL]);

  // Last 7 months including current month
  const getMonthLabels = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ label: months[d.getMonth()], year: d.getFullYear(), monthIdx: d.getMonth() });
    }
    return out;
  };

  const buildLeadsChartData = () => getMonthLabels().map(m => ({
    name: m.label,
    leads: leads.filter(l => l.leadDate && new Date(l.leadDate).getMonth() === m.monthIdx && new Date(l.leadDate).getFullYear() === m.year).length,
    sales: sales.filter(s => s.saleDate && new Date(s.saleDate).getMonth() === m.monthIdx && new Date(s.saleDate).getFullYear() === m.year).length,
  }));

  const buildEventsChartData = () => getMonthLabels().map(m => ({
    name: m.label,
    events: events.filter(e => e.eventDate && new Date(e.eventDate).getMonth() === m.monthIdx && new Date(e.eventDate).getFullYear() === m.year).length,
  }));

  const buildBudgetChartData = () => {
    if (!stats) return [];
    return [
      { name: 'Income', value: Number(stats.totalIncome) || 0 },
      { name: 'Expenses', value: Number(stats.totalExpenses) || 0 },
    ];
  };

  const BUDGET_COLORS = ['#10b981', '#ef4444'];
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;

  const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString()}`;
  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Compute tasks due today and this week (excludes completed)
  const today = new Date(); today.setHours(0,0,0,0);
  const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + 7);
  const tasksDueSoon = tasks
    .filter(t => {
      if (!t.dueDate || t.status === 'completed') return false;
      const d = new Date(t.dueDate);
      return d >= today && d <= endOfWeek;
    })
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  function formatTaskDue(dueDate) {
    const d = new Date(dueDate); d.setHours(0,0,0,0);
    const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `In ${diff} days`;
  }

  const upcomingEvents = [...events]
    .filter(e => e.eventDate)
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 5);

  const statusPillClass = (status) => {
    if (!status) return 'pill pill-neutral';
    const s = status.toLowerCase();
    if (s === 'active') return 'pill pill-success';
    if (s === 'planning' || s === 'draft' || s === 'drafts') return 'pill pill-info';
    if (s === 'completed' || s === 'complete') return 'pill pill-neutral';
    return 'pill pill-neutral';
  };

  return (
    <DashboardLayout>
      <div className="dashboard-header">
        <h1 className="page-title">{(() => {
          const h = new Date().getHours();
          const greet = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
          const name = user.firstName || (user.email ? user.email.split('@')[0] : '');
          return name ? `${greet}, ${name}` : greet;
        })()}</h1>
        <p className="dashboard-subtitle">Here's what's happening with your trade shows today.</p>
      </div>

      {!loading && tasksDueSoon.length > 0 && (
        <div className="tasks-due-strip">
          <div className="tasks-due-strip-label">
            <span className="tasks-due-strip-dot"></span>
            Due this week
          </div>
          <div className="tasks-due-strip-items">
            {tasksDueSoon.slice(0, 5).map((t, i) => (
              <button
                key={i}
                className={`tasks-due-chip ${formatTaskDue(t.dueDate) === 'Today' ? 'today' : ''}`}
                onClick={() => navigate('/tasks')}
              >
                <span className="tasks-due-chip-title">{t.taskName}</span>
                <span className="tasks-due-chip-meta">{formatTaskDue(t.dueDate)}</span>
              </button>
            ))}
            {tasksDueSoon.length > 5 && (
              <button className="tasks-due-more" onClick={() => navigate('/tasks')}>
                +{tasksDueSoon.length - 5} more
              </button>
            )}
            <button className="tasks-due-view-all" onClick={() => navigate('/tasks')}>
              View all tasks &rarr;
            </button>
          </div>
        </div>
      )}

      <div className="stats-row">
        <div className="stat-card stat-card-clickable" onClick={() => navigate('/events')} title="View all events">
          <div className="stat-card-body">
            <div className="stat-icon-wrapper events">
              <MdStar size={22} />
            </div>
            <div className="stat-info">
              <h3>Events</h3>
              <p className="stat-value">
                {loading ? '—' : stats ? stats.totalEvents : '0'}
              </p>
              <span className="stat-subtext">Total tracked</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-body">
            <div className="stat-icon-wrapper earnings">
              <MdAttachMoney size={22} />
            </div>
            <div className="stat-info">
              <h3>Annual Earnings</h3>
              <p className="stat-value">
                {loading ? '—' : stats ? fmtCurrency(stats.totalRevenue) : '$0'}
              </p>
              <span className="stat-subtext">Year to date</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-body">
            <div className="stat-icon-wrapper tasks">
              <MdFormatListBulleted size={22} />
            </div>
            <div className="stat-info">
              <h3>Tasks Completed</h3>
              <p className="stat-value">
                {loading ? '—' : `${completedTasks}`}
                {!loading && totalTasks > 0 && (
                  <span className="stat-denominator"> / {totalTasks}</span>
                )}
              </p>
              <span className="stat-subtext">
                {!loading && totalTasks > 0
                  ? `${Math.round((completedTasks / totalTasks) * 100)}% complete`
                  : 'No tasks yet'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-card-header">
            <h3>Leads &amp; Sales</h3>
            <span className="chart-card-subtitle">Last 7 months</span>
          </div>
          <div className="chart-container">
            {loading ? <p className="loading-text">Loading...</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={buildLeadsChartData()} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="leads" name="Leads" stroke="#FF6B35" strokeWidth={2.5} dot={{ r: 4, fill: '#FF6B35' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="sales" name="Sales" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card-header">
            <h3>Events Overview</h3>
            <span className="chart-card-subtitle">Last 7 months</span>
          </div>
          <div className="chart-container">
            {loading ? <p className="loading-text">Loading...</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={buildEventsChartData()} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} cursor={{ fill: '#f8fafc' }} />
                  <Bar dataKey="events" name="Events" fill="#FF6B35" radius={[6, 6, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-card-header">
            <h3>Budget Overview</h3>
            <span className="chart-card-subtitle">Income vs. expenses</span>
          </div>
          <div className="chart-container donut-container">
            {loading ? <p className="loading-text">Loading...</p> : (
              <div className="budget-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={buildBudgetChartData()} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={2} dataKey="value">
                      {buildBudgetChartData().map((entry, index) => (
                        <Cell key={index} fill={BUDGET_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => fmtCurrency(value)} contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="budget-legend">
                  <div className="budget-legend-item">
                    <span className="budget-dot" style={{ background: BUDGET_COLORS[0] }}></span>
                    <div>
                      <div className="budget-legend-label">Income</div>
                      <div className="budget-legend-value">{fmtCurrency(stats?.totalIncome)}</div>
                    </div>
                  </div>
                  <div className="budget-legend-item">
                    <span className="budget-dot" style={{ background: BUDGET_COLORS[1] }}></span>
                    <div>
                      <div className="budget-legend-label">Expenses</div>
                      <div className="budget-legend-value">{fmtCurrency(stats?.totalExpenses)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card-header">
            <h3>Upcoming Events</h3>
            <span className="chart-card-subtitle">Next 5 scheduled</span>
          </div>
          <div className="activities-list">
            {loading ? <p className="loading-text">Loading...</p> :
              upcomingEvents.length > 0 ? upcomingEvents.map((event, index) => (
                <div className="activity-row" key={index}>
                  <div className="activity-info">
                    <p className="activity-title">{event.eventName}</p>
                    <span className="activity-meta">{event.venue || 'No venue set'}</span>
                  </div>
                  <div className="activity-right">
                    <span className="activity-date-text">{fmtDate(event.eventDate)}</span>
                    {event.status && <span className={statusPillClass(event.status)}>{event.status}</span>}
                  </div>
                </div>
              )) : <p className="no-data">No upcoming events.</p>
            }
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Dashboard;
